import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import styles from './Footer.module.css';

const cols = [
  {
    title: 'Services',
    links: [
      { label: 'IT Solutions',     to: '/services' },
      { label: 'Web Development',  to: '/services' },
      { label: 'Mobile Apps',      to: '/services' },
      { label: 'Brand Strategy',   to: '/services' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About Us',    to: '/about' },
      { label: 'Our Journey', to: '/about' },
      { label: 'Contact Us',  to: '/contact-us' },
    ],
  },
];

const socials = [
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
      </svg>
    ),
  },
  {
    label: 'Twitter',
    href: 'https://x.com/',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"/>
      </svg>
    ),
  },
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/transvue-soultions-india-pvt-ltd/',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6zM2 9h4v12H2z"/>
        <circle cx="4" cy="4" r="2"/>
      </svg>
    ),
  },
];

export default function Footer() {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  
  // Parallax scroll state
  const footerRef = useRef(null);
  const [translateY, setTranslateY] = useState(60);
  const [opacity, setOpacity] = useState(0.5);

  // Mouse-following glow state
  const [glowX, setGlowX] = useState(50);
  const [isHovered, setIsHovered] = useState(false);
  const [currentGlowX, setCurrentGlowX] = useState(50);

  // Parallax Scroll logic
  useEffect(() => {
    const handleScroll = () => {
      if (!footerRef.current) return;
      const rect = footerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      if (rect.top < windowHeight) {
        const visibleHeight = windowHeight - rect.top;
        const totalHeight = rect.height || 400;
        const progress = Math.min(Math.max(visibleHeight / totalHeight, 0), 1);
        
        // Translate from 60px down to 0px
        const trans = (1 - progress) * 60;
        // Fade opacity from 0.5 to 1
        const op = 0.5 + progress * 0.5;
        
        setTranslateY(trans);
        setOpacity(op);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // init
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Smooth mouse glow transition back to center
  useEffect(() => {
    if (isHovered) {
      setCurrentGlowX(glowX);
    } else {
      // smooth interpolation back to 50%
      const timer = setTimeout(() => {
        setCurrentGlowX(prev => {
          const diff = 50 - prev;
          if (Math.abs(diff) < 1) return 50;
          return prev + diff * 0.15;
        });
      }, 16);
      return () => clearTimeout(timer);
    }
  }, [glowX, isHovered, currentGlowX]);

  const handleMouseMove = (e) => {
    if (!footerRef.current) return;
    const rect = footerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = (x / rect.width) * 100;
    setGlowX(percent);
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (!email) return;
    setSubscribed(true);
    setTimeout(() => {
      setEmail('');
      setSubscribed(false);
    }, 4000);
  };

  return (
    <footer 
      ref={footerRef} 
      className={styles.footer}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {/* Top Border with Glowing Ambient Accent that follows mouse */}
      <div 
        className={styles.glowLine} 
        style={{ 
          background: `linear-gradient(90deg, 
            transparent 0%, 
            rgba(230, 51, 41, 0.85) ${currentGlowX}%, 
            transparent 100%)`
        }} 
      />

      <div 
        className={styles.inner}
        style={{ 
          transform: `translateY(${translateY}px)`,
          opacity: opacity,
          transition: 'transform 0.15s cubic-bezier(0.25, 1, 0.5, 1), opacity 0.15s ease-out'
        }}
      >
        {/* Brand details */}
        <div className={styles.brand}>
          <Link to="/" className={styles.logoWrap}>
            <span className={styles.logo}>TSIL</span>
            <span className={styles.logoDot} />
          </Link>
          <p className={styles.tagline}>
            Building high-performance digital solutions that grow brands, engage audiences, and scale seamlessly.
          </p>
          <div className={styles.socials}>
            {socials.map(s => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.social}
                aria-label={s.label}
              >
                {s.icon}
              </a>
            ))}
          </div>
        </div>

        {/* Regular link columns */}
        {cols.map(col => (
          <div key={col.title} className={styles.col}>
            <h4 className={styles.colTitle}>{col.title}</h4>
            <ul className={styles.colList}>
              {col.links.map(l => (
                <li key={l.label}>
                  <Link to={l.to} className={styles.footLink}>
                    <span className={styles.linkText}>{l.label}</span>
                    <span className={styles.linkArrow}>↗</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}

        {/* Contact Info Column */}
        <div className={styles.col}>
          <h4 className={styles.colTitle}>Get in Touch</h4>
          <ul className={styles.contactList}>
            <li>
              <a href="mailto:team@tsil.net.in" className={styles.contactItem} title="Email us">
                <svg className={styles.contactIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                <span>team@tsil.net.in</span>
              </a>
            </li>
            <li>
              <a href="tel:+919755856913" className={styles.contactItem} title="Call us">
                <svg className={styles.contactIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                <span>+91-97558 56913</span>
              </a>
            </li>
            <li>
              <a href="tel:+919661829944" className={styles.contactItem} title="Call us">
                <svg className={styles.contactIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                <span>+91-96618 29944</span>
              </a>
            </li>
            <li>
              <div className={`${styles.contactItem} ${styles.address}`}>
                <svg className={styles.contactIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                <span>609, Tower II, Pearls Omaxe, NSP, Pitampura, New Delhi - 110034</span>
              </div>
            </li>
          </ul>
        </div>

        {/* Newsletter Signup Column */}
        <div className={styles.colNewsletter}>
          <h4 className={styles.colTitle}>Stay Updated</h4>
          <p className={styles.newsletterDesc}>
            Subscribe to our newsletter to receive the latest updates, design articles, and tech insights.
          </p>
          <form className={styles.newsletterForm} onSubmit={handleSubscribe}>
            <div className={styles.inputWrapper}>
              <input
                type="email"
                placeholder="Enter your email"
                className={styles.newsletterInput}
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
              />
              <button type="submit" className={styles.newsletterSubmit} aria-label="Subscribe">
                {subscribed ? (
                  <span className={styles.successTick}>✓</span>
                ) : (
                  <svg className={styles.submitIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                  </svg>
                )}
              </button>
            </div>
            {subscribed && <span className={styles.successMessage}>Thank you for subscribing!</span>}
          </form>
        </div>
      </div>

      {/* Bottom section with Copyright & legal links */}
      <div className={styles.bottom}>
        <div className={styles.bottomInner}>
          <span className={styles.copyright}>© {new Date().getFullYear()} TSIL. All rights reserved.</span>
          <div className={styles.legalLinks}>
            <Link to="/about" className={styles.legalLink}>Privacy Policy</Link>
            <span className={styles.separator}>•</span>
            <Link to="/about" className={styles.legalLink}>Terms of Service</Link>
            <span className={styles.separator}>•</span>
            <Link to="/about" className={styles.legalLink}>Cookie Settings</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}